/**
 * Shop-staff Capacitor native bridge — runs only when wrapped as the
 * "EMA Pickup Point" native app on iOS/Android.
 *
 * Provides:
 *   - Native push registration tied to the shop's partner JWT (so the shop
 *     gets a real native notification when a courier delivers a parcel,
 *     when a return is queued, or when the monthly statement is ready)
 *   - Deep linking from a tapped notification → relevant tab in /pakkeshop
 *
 * Web/PWA users continue using their browser's web-push or simply rely on
 * the open dashboard.
 */
import { Capacitor } from "@capacitor/core";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform();

let _registered = false;
const PENDING_KEY = "ema-shop-pending-native-token";
const SHOP_JWT_KEY = "ema_pakkeshop_token";

function _shopJwt() {
  try { return localStorage.getItem(SHOP_JWT_KEY) || ""; } catch { return ""; }
}

async function _sendTokenToBackend(token) {
  const jwt = _shopJwt();
  if (!jwt) return false;
  try {
    await axios.post(`${API}/pakkeshop/push/native-register`, {
      token,
      platform: platform(),
    }, { headers: { Authorization: `Bearer ${jwt}` } });
    return true;
  } catch (e) {
    console.warn("shop push register failed:", e?.message);
    return false;
  }
}

/**
 * Initialize push notifications for the shop-staff app shell.
 * Idempotent. Call after a successful shop login.
 */
export async function initShopNativePush() {
  if (!isNative() || _registered) return { ok: false, reason: "not-native-or-already" };
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    const perm = await PushNotifications.checkPermissions();
    let receive = perm.receive;
    if (receive === "prompt" || receive === "prompt-with-rationale") {
      const req = await PushNotifications.requestPermissions();
      receive = req.receive;
    }
    if (receive !== "granted") return { ok: false, reason: "denied" };

    await PushNotifications.register();

    const tokenPromise = new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error("Push registration timeout")), 15000);
      PushNotifications.addListener("registration", (token) => {
        clearTimeout(t);
        resolve(token.value);
      });
      PushNotifications.addListener("registrationError", (err) => {
        clearTimeout(t);
        reject(err);
      });
    });
    const token = await tokenPromise;

    const ok = await _sendTokenToBackend(token);
    if (!ok) {
      try { localStorage.setItem(PENDING_KEY, JSON.stringify({ token, platform: platform() })); } catch (e) { console.warn("Cache shop token failed:", e?.message); }
    }

    // Deep link push tap → relevant tab i pakkeshop
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const data = action.notification?.data || {};
      const tab = data.tab || "parcels";
      window.location.href = `/pakkeshop?tab=${encodeURIComponent(tab)}${data.booking_number ? `&b=${encodeURIComponent(data.booking_number)}` : ""}`;
    });

    _registered = true;
    return { ok: true, token };
  } catch (err) {
    console.error("initShopNativePush failed:", err);
    return { ok: false, reason: err?.message || String(err) };
  }
}

/**
 * Flush any token captured before the shop logged in.
 */
export async function flushPendingShopToken() {
  if (!isNative()) return;
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw);
    const ok = await _sendTokenToBackend(obj.token);
    if (ok) localStorage.removeItem(PENDING_KEY);
  } catch (e) {
    console.warn("flushPendingShopToken failed:", e);
  }
}
